import { React, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
// Компоненты пользовательского интерфейса
import FormContainer from '../CustomElements/FormContainer';
import CustomContainer from '../CustomElements/CustomContainer';
import CustomTitle from '../CustomElements/CustomTitle';
import Input from '../CustomElements/Input';
import CustomButton from '../CustomElements/CustomButton';
import { useInfocard, useLoader, useMaskedInput } from './Common';
import Selector from '../CustomElements/Selector.jsx';
// Кастомные хуки
import useOptionsLoader from '../../customHook/useOptionsLoader';
import { useButtonValidation } from '../../customHook/useButtonValidation.js';
import useFormDataChanged from '../../customHook/useFormDataChanged.js';
// Утилиты
import { handleUniversalChange } from '../../utils/formUtils.js';
import { removeEmptyStrings } from '../../utils/removeEmptyStrings.js';
import { omitFields } from '../../utils/omitFields.js';
import { convertStringValuesToNumbers } from '../../utils/convertStringValuesToNumbers.js';
import { extractChangedFields } from '../../utils/extractChangedFields.js';
// Действия Redux
import { updateInfocardFromServer, addInfocardFromServer } from '../../redux/actions/infocards.action';
import { load, remove } from '../../redux/actions/loader.action.js';
import { initDepartmentOptionsFromServer, initOptionsDirectoratesFromServer } from '../../redux/actions/options.action';

//компонент для создания и редактирования информационной карты
export default function InfocardCreationOrEditing({ closeModal, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  // получаем текущую инфокарту из стейта
  const infocard = useInfocard();
  // получаем опции из стейта
  const optionsForSelect = useSelector((state) => state.options);
  //определяем в каком режиме находимся если infocards/edit, то редактируем иначе создаем
  let loc = useLocation();
  const isEditing = useMemo(() => loc.pathname?.includes('infocards/edit'), [loc.pathname]);

  // Состояние для формы. Мемоизируем начальные данные формы
  const initialFormData = useMemo(
    () => ({
      kitId: infocard?.kitId || '',
      directorate: infocard?.directorate || '',
      stationName: infocard?.stationName || '',
      maxSpeed: infocard?.maxSpeed || '',
      road: infocard?.road || '',
      minDist: infocard?.minDist || '',
      department: infocard?.department || '',
      alertDist: infocard?.alertDist || '',
      direction: infocard?.direction,
      isStation: infocard?.isStation, // поддержка false
    }),
    [infocard]
  );
  const [formData, setFormData] = useState(initialFormData);
  // const [formErrors, setFormErrors] = useState(defVal);
  const { isLoading, isSuccess } = useLoader();

  //хук для запроса опций для дирекций и контроля селекта
  const messageForSelectDirectorates = t?.selectRoad || 'Выберите дорогу';

  const directoratesControl = useOptionsLoader(
    formData,
    setFormData,
    ['directorate', 'department'],
    'road',
    initOptionsDirectoratesFromServer,
    messageForSelectDirectorates
  );

  //хук для запроса опций для подразделений и контроля селекта
  const messageForSelectDepartments = t?.selectDirectorate || 'Выберите дирекцию';
  const departmentsControl = useOptionsLoader(
    formData,
    setFormData,
    ['department'],
    'directorate',
    initDepartmentOptionsFromServer,
    messageForSelectDepartments
  );

  // Опции для селекторов
  const directionOptions = [
    { label: 0, value: 0 },
    { label: 1, value: 1 },
  ];
  const typeOptions = [
    { label: t?.station || 'Станция', value: true },
    { label: t?.segment || 'Перегон', value: false },
  ];

  // заполняем начальные данные формы при первом рендере и при изменении инфокарты
  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, ['maxSpeed', 'minDist', 'alertDist']);
  //проверяем были ли изменения в форме
  const isFormEdit = useFormDataChanged(initialFormData, formData, isEditing);

  // Обработчик события для отправки формы
  const onApply = useCallback(() => {
    dispatch(load());
    const excludedFields = ['directorate', 'road'];
    // если редактируем
    if (isEditing) {
      // Оставляем только измененные поля
      const changedFields = extractChangedFields(infocard, formData);
      // Убираем ненужные поля из формы
      const formWithoutExcluded = omitFields(changedFields, [...excludedFields]);
      dispatch(updateInfocardFromServer(formWithoutExcluded, infocard?.stationId));
    }
    // если создаем
    if (!isEditing) {
      // Удаляем ненужные поля из формы
      const formWithoutExcluded = omitFields(formData, excludedFields);
      // Удаляем пустые строки
      const formWithoutEmptyStrings = removeEmptyStrings(formWithoutExcluded);
      // Преобразуем строковые значения в числа
      const transformedPayload = convertStringValuesToNumbers(formWithoutEmptyStrings, ['kitId']);
      dispatch(addInfocardFromServer(transformedPayload));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formData, isEditing, infocard]);

  // формируем пропсы для инпутов
  function useDefaultValues(key) {
    return {
      name: key,
      state: formData[key],
      changeState: (state) => {
        setFormData((prev) => ({
          ...prev,
          [key]: state,
        }));
      },
      width: '100%',
      height: 'var(--sadr-font-size-120)',
    };
  }

  // создаем функцию для изменения инпутов
  function createGetStateFromChange(label) {
    return (arr) => {
      const str = arr[0]?.trim();
      if (!str) {
        return { err: `${label}`, value: '' };
      }
      const value = +str;
      let err = null;
      if (!Number.isFinite(value) || value < 1) {
        err = `${label}`;
      }
      return { err, value };
    };
  }

  //массив полей для формы
  let controls = [
    <Input
      onChange={(event) => handleUniversalChange(formData, setFormData, event)}
      name="kitId"
      label={t?.kitIdLabel || 'КИТ ID *'}
      placeholder={t?.kitIdPlaceholder || 'Введите ID'}
      width="100%"
      height="var(--sadr-font-size-60)"
      value={formData?.kitId || ''}
      format="######"
      maxLength={9}
      isCoordinate={true}
      isAllowed={undefined}
    />,
    <Selector
      options={typeOptions}
      onChange={(selectedOption) => {
        handleUniversalChange(formData, setFormData, null, 'isStation', selectedOption, 'select');
      }}
      label={t?.typeLabel || 'Тип *'}
      placeholder={t?.selectType || 'Выберите тип'}
      value={typeOptions?.find((option) => option.value === formData.isStation) || null}
      name="isStation"
      isMulti={false}
    />,

    <Selector
      options={optionsForSelect.roads}
      onChange={(selectedOption) => {
        handleUniversalChange(formData, setFormData, null, 'road', selectedOption, 'select');
      }}
      isMulti={false}
      label={t?.roadLabel || 'Дорога *'}
      placeholder={t?.selectRoad || 'Выберите дорогу'}
      value={formData.road ? optionsForSelect?.roads?.find((option) => option.value === formData.road) : null}
      name="road"
    />,
    <Selector
      options={directionOptions}
      onChange={(selectedOption) => {
        handleUniversalChange(formData, setFormData, null, 'direction', selectedOption, 'select');
      }}
      value={
        formData.direction || formData?.direction === 0
          ? directionOptions?.find((option) => option.value === formData.direction)
          : null
      }
      name="direction"
      label={t?.directionLabel || 'Направление *'}
      placeholder={t?.selectDirection || 'Выберите направление'}
      isMulti={false}
    />,
    <Selector
      options={optionsForSelect?.directorates}
      onChange={(selectedOption) => {
        handleUniversalChange(formData, setFormData, null, 'directorate', selectedOption, 'select');
      }}
      label={t?.directorateLabel || 'Дирекция *'}
      placeholder={t?.selectDirectorate || 'Выберите дирекцию'}
      value={
        formData.directorate
          ? optionsForSelect?.directorates?.find((option) => option.value === formData.directorate)
          : null
      }
      isMulti={false}
      name="directorate"
      isDisabled={directoratesControl?.isSelectorDisabled}
      message={directoratesControl?.message}
      customClick={directoratesControl?.onSelectorChange}
    />,

    useMaskedInput({
      ...useDefaultValues('maxSpeed'),
      label: t?.maxSpeedLabel || 'Разрешенная макс. скорость движения',
      placeholder: t?.enterSpeed || 'Введите скорость в км/ч',
      valueToStr: (v) => v,
      getStateFromChange: createGetStateFromChange(t?.enterSpeed || 'скорость в км/ч'),
      mask: `999 ${t?.unitSpeed}`,
    }),
    <Selector
      options={optionsForSelect.departments}
      onChange={(selectedOption) => {
        handleUniversalChange(formData, setFormData, null, 'department', selectedOption, 'select');
      }}
      isMulti={false}
      label={t?.departmentLabel || 'Подразделение *'}
      placeholder={t?.selectDepartment || 'Выберите № подразделения'}
      value={
        formData.department
          ? optionsForSelect?.departments?.find((option) => option.value === formData.department)
          : null
      }
      name="department"
      isDisabled={departmentsControl?.isSelectorDisabled}
      message={departmentsControl?.message}
      customClick={departmentsControl?.onSelectorChange}
    />,

    useMaskedInput({
      ...useDefaultValues('minDist'),
      label: t?.minDistLabel || 'Минимальное расстояние оповещения',
      placeholder: t?.enterDistance || 'Введите расстояние в метрах',
      mask: '99999 м.',
      valueToStr: (v) => v,
      getStateFromChange: createGetStateFromChange(t?.enterDistance || 'расстояние в метрах'),
    }),

    <Input
      onChange={(event) => handleUniversalChange(formData, setFormData, event)}
      name="stationName"
      label={t?.stationLabel || 'Станция/перегон *'}
      placeholder={t?.enterStation || 'Введите станцию'}
      width="100%"
      height="var(--sadr-font-size-60)"
      value={formData?.stationName || ''}
      selfAlign="end"
    />,
    useMaskedInput({
      ...useDefaultValues('alertDist'),
      label: t?.alertDistLabel || 'Тревога при приближ. ПС с исправным УИППС',
      placeholder: t?.enterDistance || 'Введите расстояние в метрах',
      mask: '9999 м.',
      valueToStr: (v) => v,
      getStateFromChange: createGetStateFromChange(t?.enterDistance || 'расстояние в метрах'),
    }),
  ];

  // добавляем дополнительные пропсы и обертку для полей формы
  const editControls = controls.map((control, index) => (
    <div
      key={index}
      column={index % 2 === 0 ? 'left' : 'right'}
      selfAlign={[3, 4, 5, 6, 8].includes(index) ? 'start' : ''}
    >
      {control}
    </div>
  ));

  return (
    <CustomContainer>
      <CustomTitle
        titleText={
          isEditing
            ? t?.editInfocardTitle || 'РЕДАКТИРОВАНИЕ ИНФОРМАЦИОННОЙ КАРТЫ'
            : t?.createInfocardTitle || 'СОЗДАНИЕ НОВОЙ ИНФОРМАЦИОННОЙ КАРТЫ'
        }
      />
      {(!isLoading || isLoading) && !isSuccess && (
        <CustomContainer>
          <FormContainer className="form-container_form w-100">
            {editControls}
            <CustomButton
              width="var(--sadr-font-size-420)"
              height="var(--sadr-font-size-60)"
              text={
                isEditing
                  ? t?.editInfocard || 'Изменить информационную карту'
                  : t?.addInfocard || 'Добавить информационную карту'
              }
              onClick={onApply}
              disabled={isButtonDisabled || !isFormEdit}
              isButton
            />
          </FormContainer>
        </CustomContainer>
      )}
      {!isLoading && isSuccess && (
        <div className="d-flex flex-column flex-nowrap mx-auto my-auto">
          <span className="label-success">
            {isEditing
              ? t?.infocardUpdated || 'Информационная карта изменена'
              : t?.infocardCreated || 'Новая информационная карта добавлена'}
          </span>
          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={
              isEditing ? t?.goToInfocard || 'К информационной карте' : t?.goToInfocards || 'К информационным картам'
            }
            onClick={() => {
              closeModal(true);
              dispatch(remove());
            }}
            isButton
          />
        </div>
      )}
    </CustomContainer>
  );
}
